import { useState } from "react";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { nexusApi } from "@/context/NexusAuthContext";
import "@/pages/nexus/nexus.css";

const empty = {
  contact_name: "", contact_email: "", contact_phone: "", company: "", role: "",
  asset_type: "", jurisdiction: "", regulator: "", regulatory_status: "",
  description: "", asking_price: "",
  authority_confirmed: false, accuracy_confirmed: false,
  website: "",
};

export default function NexusSell() {
  const { t } = useTranslation();
  const [form, setForm] = useState(empty);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await nexusApi.post("/seller-submissions", form);
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("nexus.sell.couldNotSubmit"));
    } finally {
      setSending(false);
    }
  };

  if (sent) return (
    <div className="nx-container py-24 text-center max-w-2xl mx-auto" data-testid="nexus-sell-sent">
      <CheckCircle2 size={40} className="text-[#7FB08A] mx-auto mb-5" />
      <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">{t("nexus.sell.sentTitle")}</h1>
      <p className="text-white/85 mb-6 leading-relaxed">
        {t("nexus.sell.sentBody")}
      </p>
    </div>
  );

  const preformParts = t("nexus.sell.preformNotice").split(/\*\*(.*?)\*\*/);
  return (
    <div data-testid="nexus-sell">
      <section className="nx-hero" style={{ padding: "72px 0 40px" }}>
        <div className="nx-container">
          <div className="nx-eyebrow mb-3">{t("nexus.sell.eyebrow")}</div>
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">{t("nexus.sell.title")}</h1>
          <p className="text-white/75 text-lg max-w-2xl">
            {t("nexus.sell.lead")}
          </p>
        </div>
      </section>
      <section className="nx-container py-12">
        <div className="max-w-4xl mb-8 p-6 border border-[var(--line-2)]" data-testid="sell-preform-notice">
          <p className="text-[var(--bone-2)] leading-relaxed">
            {preformParts.map((part, i) =>
              i % 2 === 1 ? (
                <strong key={i} className="text-[var(--bone)] font-normal">{part}</strong>
              ) : (
                <span key={i}>{part}</span>
              )
            )}
          </p>
        </div>

        <form onSubmit={submit} className="grid gap-6 md:grid-cols-2 max-w-4xl">
          <Field label={`${t("nexus.sell.yourName")} *`} name="contact_name" value={form.contact_name} onChange={onChange} required />
          <Field label={`${t("nexus.sell.email")} *`} name="contact_email" type="email" value={form.contact_email} onChange={onChange} required />
          <Field label={t("nexus.sell.phone")} name="contact_phone" value={form.contact_phone} onChange={onChange} />
          <Field label={t("nexus.sell.company")} name="company" value={form.company} onChange={onChange} />
          <Field label={t("nexus.sell.yourRole")} name="role" value={form.role} onChange={onChange} placeholder={t("nexus.sell.yourRolePh")} />
          <div className="hidden md:block"></div>

          <div className="md:col-span-2 nx-h-rule" />

          <Field label={`${t("nexus.sell.assetType")} *`} name="asset_type" value={form.asset_type} onChange={onChange} required placeholder={t("nexus.sell.assetTypePh")} />
          <Field label={`${t("nexus.sell.jurisdiction")} *`} name="jurisdiction" value={form.jurisdiction} onChange={onChange} required />
          <Field label={t("nexus.sell.regulator")} name="regulator" value={form.regulator} onChange={onChange} />
          <Field label={t("nexus.sell.regulatoryStatus")} name="regulatory_status" value={form.regulatory_status} onChange={onChange} />
          <div className="md:col-span-2">
            <label className="nx-label">{t("nexus.sell.description")} *</label>
            <textarea
              name="description" required maxLength={1500} rows={5}
              value={form.description} onChange={onChange}
              placeholder={t("nexus.sell.descriptionPh")}
              className="nx-field" data-testid="sell-description"
            />
            <div className="text-xs text-white/40 mt-1 font-mono">{form.description.length}/1500</div>
          </div>
          <Field label={t("nexus.sell.askingPrice")} name="asking_price" value={form.asking_price} onChange={onChange} placeholder={t("nexus.sell.askingPricePh")} />

          <div className="md:col-span-2 space-y-3 mt-2">
            <label className="flex items-start gap-3 text-white/85 text-sm">
              <input
                type="checkbox"
                name="authority_confirmed"
                checked={form.authority_confirmed}
                onChange={(e) => { onChange(e); setForm((f) => ({ ...f, accuracy_confirmed: e.target.checked })); }}
                required
                data-testid="sell-authority"
              />
              <span>{t("nexus.sell.authorityConfirm")}</span>
            </label>
          </div>

          {/* honeypot */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px" }}>
            <input tabIndex={-1} autoComplete="off" type="text" name="website" value={form.website} onChange={onChange} />
          </div>

          <div className="md:col-span-2 flex flex-wrap items-center gap-4 pt-2">
            <button type="submit" className="nx-btn-primary" disabled={sending} data-testid="sell-submit">
              {sending ? t("nexus.sell.submitting") : <>{t("nexus.sell.submit")} <ArrowRight size={14} /></>}
            </button>
            <span className="text-white/50 text-xs font-mono uppercase tracking-[0.2em]">{t("nexus.sell.confidentialNote")}</span>
          </div>
        </form>

        <p className="max-w-4xl mt-8 text-xs text-[var(--bone-2)]/55" data-testid="sell-postform-notice">
          {t("nexus.sell.postformNotice")}
        </p>
      </section>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="nx-label">{label}</label>
      <input className="nx-field" {...props} />
    </div>
  );
}
